import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { getUserPreferenceList, selectUserCategoryPreferences, selectUserPreference } from 'redux/UserPreferences';
import { setChartSet, updateChartSet } from 'redux/actions';
import { selectUserPreferences, selectChartSet } from 'redux/selectors';
import { fallbackByCategory } from '../constants';

const fallbackIds = fallbackByCategory[USER_PREFERENCE_SETS.CHART_SET];

function useChartSetPreference() {
  const dispatch = useDispatch();
  const [fallbackId] = fallbackIds;
  const { defaultId, ids, loaded, loading } = useSelector((state) =>
    selectUserCategoryPreferences(selectUserPreferences(state), USER_PREFERENCE_SETS.CHART_SET)
  );
  const chartPreferenceListLoaded = useSelector(
    (state) => selectUserCategoryPreferences(selectUserPreferences(state), USER_PREFERENCE_SETS.CHART).loaded
  );
  const selectedId = useSelector((state) => selectChartSet(state).preferenceId);
  const selectedPreference = useSelector((state) => selectUserPreference(selectUserPreferences(state), selectedId));
  const select = useCallback(
    (preference) => {
      dispatch(setChartSet(preference));
    },
    [dispatch]
  );
  const update = useCallback(
    (preference) => {
      dispatch(updateChartSet(preference));
    },
    [dispatch]
  );

  useEffect(() => {
    if (loaded) {
      return;
    }

    dispatch(getUserPreferenceList({ category: USER_PREFERENCE_SETS.CHART_SET }));
  }, [dispatch, loaded]);

  useEffect(() => {
    if (chartPreferenceListLoaded) {
      return;
    }

    dispatch(getUserPreferenceList({ category: USER_PREFERENCE_SETS.CHART }));
  }, [dispatch, chartPreferenceListLoaded]);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    // set selected id to default or fallback if none is selected
    // make sure selected id is present in loaded ids or fallback ids
    // if chart set preference is selected and user deletes it we need to switch back to default or fallback
    if (!selectedId || (!ids.includes(selectedId) && !fallbackIds.includes(selectedId))) {
      select(defaultId ?? fallbackId);
    }
  }, [select, loaded, ids, selectedId, defaultId, fallbackId]);

  return {
    ids,
    fallbackIds,
    selectedId,
    defaultId,
    fallbackId,
    data: selectedPreference,
    loading,
    loaded,
    select,
    update
  };
}

export default useChartSetPreference;
